import type { EquippedCosmetic, HubFacing, HubPresenceEntry } from '../api/types';
import { versionedAssetUrl } from '../assets/version';
import { appearanceAssetIdForCosmetics, playerSkinSpriteUrl } from '../cosmetics/visuals';
import { emoteFrameAt, emoteSpriteUrl, HUB_EMOTE_BUBBLE_ASSET_ID, hubEmoteById } from '../hub/emotes';
import type { AppState } from '../state/store';

export type HubDirection = HubFacing;

export interface HubPosition {
  x: number;
  y: number;
}

export type HubStationId =
  | 'run'
  | 'inventory'
  | 'merchant'
  | 'marketplace'
  | 'cashout'
  | 'spinner'
  | 'cosmetics';

export interface HubStation {
  id: HubStationId;
  title: string;
  note: string;
  openLabel: string;
  primaryAct: string;
  footprint: Rect;
  color: string;
  prop: {
    assetId: HubStationPropId;
    sourceSize: number;
    anchorOffsetTiles?: { x: number; y: number };
  };
  labelAnchor: HubPosition;
  secondary?: { label: string; act: string };
  devOnly?: boolean;
}

export type HubStationPropId =
  | 'hub_station_dungeon_gate_01'
  | 'hub_station_inventory_03'
  | 'merchant_station_01'
  | 'hub_station_marketplace_02'
  | 'hub_station_gold_exchange_01'
  | 'hub_station_spinner_03'
  | 'hub_station_cosmetics_01';

export const HUB_STATION_PROP_ASSETS: Record<HubStationPropId, string> = {
  hub_station_dungeon_gate_01: '/assets/sprites/hub/hub_station_dungeon_gate_01.png',
  hub_station_inventory_03: '/assets/sprites/hub/hub_station_inventory_03.png',
  merchant_station_01: '/assets/sprites/merchant/merchant_station_01.png',
  hub_station_marketplace_02: '/assets/sprites/hub/hub_station_marketplace_02.png',
  hub_station_gold_exchange_01: '/assets/sprites/hub/hub_station_gold_exchange_01.png',
  hub_station_spinner_03: '/assets/sprites/hub/hub_station_spinner_03.png',
  hub_station_cosmetics_01: '/assets/sprites/hub/hub_station_cosmetics_01.png',
};

export type HubDecorPropId =
  | 'hub_prop_lantern_post_01'
  | 'hub_prop_ferryman_statue_01'
  | 'hub_prop_brazier_02'
  | 'hub_prop_candle_cluster_01'
  | 'hub_prop_crates_01';

type HubEnvironmentPropId = HubDecorPropId | 'hub_prop_signpost_01' | 'hub_prop_blackwater_pier_01';

export const HUB_ENVIRONMENT_PROP_ASSETS: Record<HubEnvironmentPropId, string> = {
  hub_prop_blackwater_pier_01: '/assets/sprites/hub/hub_prop_blackwater_pier_01.png',
  hub_prop_lantern_post_01: '/assets/sprites/hub/hub_prop_lantern_post_01.png',
  hub_prop_ferryman_statue_01: '/assets/sprites/hub/hub_prop_ferryman_statue_01.png',
  hub_prop_brazier_02: '/assets/sprites/hub/hub_prop_brazier_02.png',
  hub_prop_candle_cluster_01: '/assets/sprites/hub/hub_prop_candle_cluster_01.png',
  hub_prop_crates_01: '/assets/sprites/hub/hub_prop_crates_01.png',
  hub_prop_signpost_01: '/assets/sprites/hub/hub_prop_signpost_01.png',
};

const WAYSTATION_CAMERA_ZOOM = 1.42;
const WAYSTATION_BASE_TILE_TARGET = 56;
const WAYSTATION_ZOOMED_TILE_TARGET = WAYSTATION_BASE_TILE_TARGET * WAYSTATION_CAMERA_ZOOM;
const WAYSTATION_MIN_COLS = 13;
const WAYSTATION_MAX_COLS = 18;
const WAYSTATION_MIN_ROWS = 7;
const WAYSTATION_MAX_ROWS = 10;
export const HUB_LABEL_SIGNPOST_ENABLED = false;
export const HUB_LABEL_BACKING_ENABLED = false;
export const HUB_LABEL_WAX_SEAL_ENABLED = false;

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface HubDecorProp {
  assetId: HubDecorPropId;
  position: HubPosition;
  drawWidthTiles: number;
  sourceWidth: number;
  sourceHeight: number;
  footprint?: Rect;
  blocksMovement?: boolean;
  label?: never;
}

interface CameraViewport {
  cols: number;
  rows: number;
}

interface PlazaMetrics {
  tile: number;
  cols: number;
  rows: number;
  camera: HubPosition;
  originX: number;
  originY: number;
  width: number;
  height: number;
}

export interface HubMoveResult {
  position: HubPosition;
  facing: HubFacing;
  moved: boolean;
}

const MAP_WIDTH = 38;
const MAP_HEIGHT = 24;
const PLAYER_IMAGE_URL = '/assets/sprites/player/player.png';
const PLAYER_FRAME = 96;
/**
 * Every player skin sheet is normalized at composition time to the accepted
 * LP4d/e convention: 96×96 frames, content bbox ≈69px tall, feet (contact row)
 * at y=83, centered on x=48. Anchoring that contact row — not the frame bottom
 * — to the foot ring keeps every skin standing on its ring.
 */
const PLAYER_SHEET_CONTACT_Y = 83;
/**
 * One scale for ALL Waystation actors. Local and remote players MUST render
 * at the same size: a per-path scale split (the old self 1.35 / remote 1.12)
 * makes every client see itself bigger than everyone else sees them.
 */
const WAYSTATION_ACTOR_TILE_SCALE = 1.35;
const WAYSTATION_IDLE_FRAMES = [0, 1, 2, 3] as const;
const WAYSTATION_IDLE_FPS = 4;

/**
 * Shared draw geometry for every Waystation player actor (self and remote).
 * `feetY` is the ring/contact-shadow center; the returned rect places the
 * sheet's contact row exactly on it, horizontally centered on `cx`.
 * Exported (pure) so tests can prove both actor paths share one baseline.
 */
export function waystationActorGeometry(
  tile: number,
  cx: number,
  feetY: number,
): { dx: number; dy: number; width: number; height: number } {
  const height = tile * WAYSTATION_ACTOR_TILE_SCALE;
  const width = height;
  return {
    dx: Math.round(cx - width / 2),
    dy: Math.round(feetY - height * (PLAYER_SHEET_CONTACT_Y / PLAYER_FRAME)),
    width,
    height,
  };
}

/**
 * Contact-ring geometry shared by self and remote actors. The self highlight
 * is a stroke on the SAME ellipse — it must never inflate the local actor's
 * apparent footprint.
 */
export function waystationActorRingGeometry(tile: number): { radiusX: number; radiusY: number } {
  return { radiusX: tile * 0.38, radiusY: tile * 0.16 };
}
const HUB_TILE_ASSETS = {
  floor: '/assets/sprites/hub/hub_floor_stone_01.png',
  path: '/assets/sprites/hub/hub_floor_path_01.png',
  wall: '/assets/sprites/hub/hub_wall_arch_01.png',
};

const WALL_RECTS: Rect[] = [
  { x: 0, y: 0, width: MAP_WIDTH, height: 1 },
  { x: 0, y: 0, width: 1, height: MAP_HEIGHT },
  { x: MAP_WIDTH - 1, y: 0, width: 1, height: MAP_HEIGHT },
  { x: 0, y: MAP_HEIGHT - 2, width: MAP_WIDTH, height: 2 },
];

export const HUB_STATIONS: HubStation[] = [
  {
    id: 'run',
    title: 'Dungeon Gate',
    note: 'Echo and Reward',
    openLabel: 'Enter Dungeon Gate',
    primaryAct: 'start-echo',
    secondary: { label: 'Reward', act: 'start-reward' },
    footprint: { x: 17, y: 3, width: 5, height: 3 },
    color: '#5d406f',
    prop: { assetId: 'hub_station_dungeon_gate_01', sourceSize: 256 },
    labelAnchor: { x: 19.5, y: 3.05 },
  },
  {
    id: 'inventory',
    title: 'Inventory',
    note: 'Stash and equipment',
    openLabel: 'Open Inventory',
    primaryAct: 'goto-inventory',
    footprint: { x: 6, y: 5, width: 4, height: 2 },
    color: '#7d673c',
    prop: { assetId: 'hub_station_inventory_03', sourceSize: 256 },
    labelAnchor: { x: 8, y: 5.05 },
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    note: 'Sealed item trade',
    openLabel: 'Open Marketplace',
    primaryAct: 'goto-marketplace',
    footprint: { x: 6, y: 16, width: 4, height: 2 },
    color: '#6a3d32',
    prop: { assetId: 'hub_station_marketplace_02', sourceSize: 128 },
    labelAnchor: { x: 8, y: 16.05 },
  },
  {
    id: 'merchant',
    title: 'Merchant',
    note: 'Supplies and repair',
    openLabel: 'Open Merchant',
    primaryAct: 'goto-merchant',
    // Centered in the bottom dark-tile recess/bay (rows 19–21 below the
    // Gate/Spinner column): the prop base rests on the dark edge strip above
    // the black water, approached from the row-18 path or the open floor on
    // either side. Footprint = collision, matching the visible prop.
    footprint: { x: 17, y: 19, width: 5, height: 3 },
    color: '#596657',
    prop: { assetId: 'merchant_station_01', sourceSize: 256 },
    labelAnchor: { x: 19.5, y: 19.05 },
  },
  {
    id: 'cashout',
    title: 'Gold Exchange',
    note: 'Gold orders',
    openLabel: 'Open Gold Exchange',
    primaryAct: 'goto-cashout',
    footprint: { x: 29, y: 16, width: 3, height: 2 },
    color: '#80612b',
    prop: { assetId: 'hub_station_gold_exchange_01', sourceSize: 256 },
    labelAnchor: { x: 30.5, y: 16.05 },
  },
  {
    id: 'spinner',
    title: "Ferryman's Wager",
    note: 'Free and paid spins',
    openLabel: "Open Ferryman's Wager",
    primaryAct: 'goto-spinner',
    footprint: { x: 17, y: 8, width: 5, height: 3 },
    color: '#4a5f6f',
    prop: { assetId: 'hub_station_spinner_03', sourceSize: 256 },
    labelAnchor: { x: 19.5, y: 8.05 },
  },
  {
    id: 'cosmetics',
    title: 'Cosmetics',
    note: 'Reskins and emotes',
    openLabel: 'Open Cosmetics',
    primaryAct: 'goto-cosmetics',
    footprint: { x: 29, y: 5, width: 3, height: 2 },
    color: '#526f49',
    prop: { assetId: 'hub_station_cosmetics_01', sourceSize: 256 },
    labelAnchor: { x: 30.5, y: 5.05 },
  },
];

export const HUB_DECOR_PROPS: HubDecorProp[] = [
  {
    assetId: 'hub_prop_lantern_post_01',
    position: { x: 13.5, y: 11.4 },
    drawWidthTiles: 1.2,
    sourceWidth: 96,
    sourceHeight: 96,
    footprint: { x: 13, y: 11, width: 1, height: 1 },
    blocksMovement: true,
  },
  {
    assetId: 'hub_prop_ferryman_statue_01',
    position: { x: 24.6, y: 11.8 },
    drawWidthTiles: 2.2,
    sourceWidth: 128,
    sourceHeight: 128,
    // The statue's visible base sits in row 11 only (art anchor y=11.8); blocking
    // row 12 as well created an invisible wall on the visually open floor south
    // of the statue.
    footprint: { x: 24, y: 11, width: 2, height: 1 },
    blocksMovement: true,
  },
  {
    assetId: 'hub_prop_brazier_02',
    position: { x: 19.5, y: 13.5 },
    drawWidthTiles: 1.45,
    sourceWidth: 96,
    sourceHeight: 96,
    footprint: { x: 19, y: 13, width: 1, height: 1 },
    blocksMovement: true,
  },
  {
    assetId: 'hub_prop_candle_cluster_01',
    position: { x: 14.4, y: 17.3 },
    drawWidthTiles: 1.1,
    sourceWidth: 96,
    sourceHeight: 96,
  },
  {
    assetId: 'hub_prop_crates_01',
    position: { x: 25.7, y: 18.1 },
    drawWidthTiles: 1.65,
    sourceWidth: 96,
    sourceHeight: 96,
    footprint: { x: 25, y: 17, width: 2, height: 1 },
    blocksMovement: true,
  },
];

const SPAWN_RING: HubPosition[] = [
  { x: 18, y: 13 },
  { x: 20, y: 13 },
  { x: 18, y: 14 },
  { x: 20, y: 14 },
  { x: 17, y: 13 },
  { x: 21, y: 13 },
  { x: 17, y: 14 },
  { x: 21, y: 14 },
  { x: 18, y: 12 },
  { x: 20, y: 12 },
  { x: 16, y: 13 },
  { x: 22, y: 13 },
  { x: 16, y: 14 },
  { x: 22, y: 14 },
];

const playerImages = new Map<string, HTMLImageElement>();
const tileImages: Partial<Record<keyof typeof HUB_TILE_ASSETS, HTMLImageElement>> = {};
const hubImages = new Map<string, HTMLImageElement>();

function hashText(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function inRect(pos: HubPosition, rect: Rect): boolean {
  return pos.x >= rect.x && pos.x < rect.x + rect.width && pos.y >= rect.y && pos.y < rect.y + rect.height;
}

function adjacentToRect(pos: HubPosition, rect: Rect): boolean {
  if (inRect(pos, rect)) return false;
  return (
    pos.x >= rect.x - 1 &&
    pos.x <= rect.x + rect.width &&
    pos.y >= rect.y - 1 &&
    pos.y <= rect.y + rect.height
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function finiteInt(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : null;
}

export function stationAtHubTile(pos: HubPosition, devMode = true): HubStation | null {
  return HUB_STATIONS.find((station) => (devMode || !station.devOnly) && inRect(pos, station.footprint)) ?? null;
}

export function screenPointForHubWorld(
  pos: HubPosition,
  metrics: { tile: number; camera: HubPosition; originX: number; originY: number },
): { x: number; y: number } {
  return {
    x: metrics.originX + (pos.x - metrics.camera.x) * metrics.tile,
    y: metrics.originY + (pos.y - metrics.camera.y) * metrics.tile,
  };
}

export function visibleStationLabels(
  viewport: { camera: HubPosition; cols: number; rows: number },
  devMode = true,
): HubStation[] {
  const maxX = viewport.camera.x + viewport.cols;
  const maxY = viewport.camera.y + viewport.rows;
  return HUB_STATIONS.filter((station) => {
    if (station.devOnly && !devMode) return false;
    const anchor = station.labelAnchor;
    return anchor.x >= viewport.camera.x && anchor.x <= maxX && anchor.y >= viewport.camera.y && anchor.y <= maxY;
  });
}

function decorAtHubTile(pos: HubPosition): HubDecorProp | null {
  return HUB_DECOR_PROPS.find((prop) => prop.blocksMovement && prop.footprint && inRect(pos, prop.footprint)) ?? null;
}

export const WAYSTATION_MAP = {
  width: MAP_WIDTH,
  height: MAP_HEIGHT,
  stations: HUB_STATIONS,
  isWalkable(pos: HubPosition, devMode = true): boolean {
    if (pos.x < 0 || pos.y < 0 || pos.x >= MAP_WIDTH || pos.y >= MAP_HEIGHT) return false;
    if (WALL_RECTS.some((rect) => inRect(pos, rect))) return false;
    if (stationAtHubTile(pos, devMode)) return false;
    if (decorAtHubTile(pos)) return false;
    return true;
  },
};

export function defaultHubFacing(accountId: string): HubFacing {
  const facings: HubFacing[] = ['south', 'east', 'west', 'north'];
  return facings[hashText(accountId) % facings.length]!;
}

export function spawnHubPosition(accountId: string, index = 0, devMode = true): HubPosition {
  const start = (hashText(accountId) + index) % SPAWN_RING.length;
  for (let i = 0; i < SPAWN_RING.length; i += 1) {
    const pos = SPAWN_RING[(start + i) % SPAWN_RING.length]!;
    if (WAYSTATION_MAP.isWalkable(pos, devMode)) return { ...pos };
  }
  return { x: 18, y: 13 };
}

export function stationById(id: string | null | undefined): HubStation | null {
  return HUB_STATIONS.find((station) => station.id === id) ?? null;
}

export function nearbyStationForPosition(pos: HubPosition, devMode = true): HubStation | null {
  return HUB_STATIONS.find((station) => (devMode || !station.devOnly) && adjacentToRect(pos, station.footprint)) ?? null;
}

export function moveHubPosition(pos: HubPosition, direction: HubDirection, devMode = true): HubMoveResult {
  const delta: Record<HubDirection, HubPosition> = {
    north: { x: 0, y: -1 },
    south: { x: 0, y: 1 },
    west: { x: -1, y: 0 },
    east: { x: 1, y: 0 },
  };
  const next = { x: pos.x + delta[direction].x, y: pos.y + delta[direction].y };
  if (!WAYSTATION_MAP.isWalkable(next, devMode)) return { position: { ...pos }, facing: direction, moved: false };
  return { position: next, facing: direction, moved: true };
}

export function cameraForHubPosition(pos: HubPosition, viewport: CameraViewport): HubPosition {
  return {
    x: clamp(pos.x - Math.floor(viewport.cols / 2), 0, Math.max(0, MAP_WIDTH - viewport.cols)),
    y: clamp(pos.y - Math.floor(viewport.rows / 2), 0, Math.max(0, MAP_HEIGHT - viewport.rows)),
  };
}

// ── Remote presence render state ─────────────────────────────────────────────
//
// Presence polling is coarse (~1 s) and best-effort, so remote players are NOT
// drawn straight from the latest roster snapshot. Each remote player keeps a
// small render entry that (a) eases toward the last known tile instead of
// teleporting, (b) survives missed polls for a grace window (fading out) instead
// of vanishing, and (c) never falls back to a roster-index-based position (the
// index shifts as the roster changes, which used to scatter players around).

export interface RemotePresenceRenderEntry {
  accountId: string;
  walletLabel: string;
  cosmetics: EquippedCosmetic[];
  emote: string | null;
  /** Epoch ms window of the active timed emote bubble (null when none). */
  emoteStartedAtMs: number | null;
  emoteExpiresAtMs: number | null;
  /** Smoothed render position (float tiles), eased toward the target. */
  renderX: number;
  renderY: number;
  /** Last position reported by the server. */
  targetX: number;
  targetY: number;
  facing: HubFacing;
  /** Epoch ms of the last poll that included this account. */
  lastRosterMs: number;
}

/** Keep rendering a remote player this long after they drop out of a poll. */
export const REMOTE_PRESENCE_GRACE_MS = 12_000;
/** Start fading a remote player once they have been absent this long. */
export const REMOTE_PRESENCE_FADE_START_MS = 4_000;
const REMOTE_PRESENCE_BASE_ALPHA = 0.88;
const REMOTE_PRESENCE_MIN_ALPHA = 0.3;
const REMOTE_MOVE_TILES_PER_SEC = 6;
/** Jumps beyond this are real teleports (reconnect/refresh) — snap, don't slide. */
const REMOTE_SNAP_DISTANCE_TILES = 6;

function clampToHubBounds(pos: HubPosition): HubPosition {
  return {
    x: clamp(pos.x, 1, MAP_WIDTH - 2),
    y: clamp(pos.y, 1, MAP_HEIGHT - 3),
  };
}

/**
 * Resolve the tile a remote player should head toward. Reported coordinates are
 * used as-is (clamped to the map) — never re-derived from the roster index; if a
 * report carries no coordinates, the previous target (or a stable per-account
 * spawn) is kept so the player does not jump.
 */
export function remotePresenceTarget(
  entry: HubPresenceEntry,
  previous?: Pick<RemotePresenceRenderEntry, 'targetX' | 'targetY'>,
): HubPosition {
  const x = finiteInt(entry.x);
  const y = finiteInt(entry.y);
  if (x !== null && y !== null) return clampToHubBounds({ x, y });
  if (previous) return { x: previous.targetX, y: previous.targetY };
  return spawnHubPosition(entry.accountId, 0);
}

function parseEpochMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/** Fold a fresh roster poll into the render state (mutates and returns `state`). */
export function syncRemotePresences(
  state: Map<string, RemotePresenceRenderEntry>,
  roster: readonly HubPresenceEntry[],
  selfId: string | null | undefined,
  nowMs: number,
): Map<string, RemotePresenceRenderEntry> {
  const seen = new Set<string>();
  for (const entry of roster) {
    if (!entry.accountId || entry.accountId === selfId) continue;
    seen.add(entry.accountId);
    const previous = state.get(entry.accountId);
    const target = remotePresenceTarget(entry, previous);
    const facing = isHubFacing(entry.facing) ? entry.facing : previous?.facing ?? defaultHubFacing(entry.accountId);
    if (previous) {
      previous.walletLabel = entry.walletLabel;
      previous.cosmetics = entry.cosmetics;
      previous.emote = entry.emote;
      previous.emoteStartedAtMs = parseEpochMs(entry.emoteStartedAt);
      previous.emoteExpiresAtMs = parseEpochMs(entry.emoteExpiresAt);
      previous.targetX = target.x;
      previous.targetY = target.y;
      previous.facing = facing;
      previous.lastRosterMs = nowMs;
    } else {
      state.set(entry.accountId, {
        accountId: entry.accountId,
        walletLabel: entry.walletLabel,
        cosmetics: entry.cosmetics,
        emote: entry.emote,
        emoteStartedAtMs: parseEpochMs(entry.emoteStartedAt),
        emoteExpiresAtMs: parseEpochMs(entry.emoteExpiresAt),
        renderX: target.x,
        renderY: target.y,
        targetX: target.x,
        targetY: target.y,
        facing,
        lastRosterMs: nowMs,
      });
    }
  }
  if (selfId) state.delete(selfId);
  for (const [accountId, entry] of state) {
    if (!seen.has(accountId) && nowMs - entry.lastRosterMs > REMOTE_PRESENCE_GRACE_MS) {
      state.delete(accountId);
    }
  }
  return state;
}

/** Ease every render position toward its target by `dtMs` of movement. */
export function advanceRemotePresences(state: Map<string, RemotePresenceRenderEntry>, dtMs: number): void {
  const step = (Math.max(0, dtMs) / 1000) * REMOTE_MOVE_TILES_PER_SEC;
  for (const entry of state.values()) {
    const dx = entry.targetX - entry.renderX;
    const dy = entry.targetY - entry.renderY;
    const dist = Math.hypot(dx, dy);
    if (dist === 0) continue;
    if (dist > REMOTE_SNAP_DISTANCE_TILES || dist <= step) {
      entry.renderX = entry.targetX;
      entry.renderY = entry.targetY;
      continue;
    }
    entry.renderX += (dx / dist) * step;
    entry.renderY += (dy / dist) * step;
  }
}

/** Draw alpha for a remote player: full while fresh, fading through the grace window. */
export function remotePresenceAlpha(entry: Pick<RemotePresenceRenderEntry, 'lastRosterMs'>, nowMs: number): number {
  const age = nowMs - entry.lastRosterMs;
  if (age <= REMOTE_PRESENCE_FADE_START_MS) return REMOTE_PRESENCE_BASE_ALPHA;
  const fadeSpan = REMOTE_PRESENCE_GRACE_MS - REMOTE_PRESENCE_FADE_START_MS;
  const t = clamp((age - REMOTE_PRESENCE_FADE_START_MS) / fadeSpan, 0, 1);
  return REMOTE_PRESENCE_BASE_ALPHA - t * (REMOTE_PRESENCE_BASE_ALPHA - REMOTE_PRESENCE_MIN_ALPHA);
}

// The live render state used by the plaza canvas (module-local by design: it is
// throwaway visual state, not app state, and resets on view teardown/tests).
const remotePresenceRenderState = new Map<string, RemotePresenceRenderEntry>();
let remotePresenceLastAdvanceMs: number | null = null;

export function resetRemotePresenceRenderState(): void {
  remotePresenceRenderState.clear();
  remotePresenceLastAdvanceMs = null;
}

function currentRemotePresences(state: AppState, nowMs: number): Map<string, RemotePresenceRenderEntry> {
  const selfId = state.hub?.self.accountId ?? null;
  const roster = state.hub?.access === 'full' ? state.hub.presence.slice(0, 50) : [];
  syncRemotePresences(remotePresenceRenderState, roster, selfId, nowMs);
  const dt = remotePresenceLastAdvanceMs === null ? 0 : nowMs - remotePresenceLastAdvanceMs;
  remotePresenceLastAdvanceMs = nowMs;
  advanceRemotePresences(remotePresenceRenderState, dt);
  return remotePresenceRenderState;
}

export function hubPositionForPresence(entry: HubPresenceEntry, index: number, devMode = true): { position: HubPosition; facing: HubFacing } {
  const x = finiteInt(entry.x);
  const y = finiteInt(entry.y);
  const explicit = x !== null && y !== null ? { x, y } : null;
  const facing = isHubFacing(entry.facing) ? entry.facing : defaultHubFacing(entry.accountId);
  if (explicit && WAYSTATION_MAP.isWalkable(explicit, devMode)) return { position: explicit, facing };
  return { position: spawnHubPosition(entry.accountId, index, devMode), facing };
}

export function localHubPosition(state: AppState): { position: HubPosition; facing: HubFacing } {
  if (state.hubPosition && WAYSTATION_MAP.isWalkable(state.hubPosition, state.devMode)) {
    return { position: state.hubPosition, facing: state.hubFacing };
  }
  const selfId = state.hub?.self.accountId;
  const selfPresence = selfId ? state.hub?.presence.find((entry) => entry.accountId === selfId) : null;
  if (selfPresence) return hubPositionForPresence(selfPresence, 0, state.devMode);
  return { position: spawnHubPosition(selfId ?? state.account?.account.id ?? 'self', 0, state.devMode), facing: 'south' };
}

export function isHubFacing(value: unknown): value is HubFacing {
  return value === 'north' || value === 'south' || value === 'east' || value === 'west';
}

export function playerSpriteForCosmetics(cosmetics: readonly EquippedCosmetic[] | null | undefined): string {
  return playerSkinSpriteUrl(appearanceAssetIdForCosmetics(cosmetics)) ?? PLAYER_IMAGE_URL;
}

export function waystationIdleFrameAt(nowMs: number): number {
  const safeNow = Number.isFinite(nowMs) ? Math.max(0, nowMs) : 0;
  const step = Math.floor(safeNow / (1000 / WAYSTATION_IDLE_FPS));
  return WAYSTATION_IDLE_FRAMES[step % WAYSTATION_IDLE_FRAMES.length]!;
}

function loadPlayerImage(url = PLAYER_IMAGE_URL): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null;
  const cached = playerImages.get(url);
  if (cached) return cached;
  const image = new Image();
  image.src = versionedAssetUrl(url);
  playerImages.set(url, image);
  return image;
}

function loadTileImage(key: keyof typeof HUB_TILE_ASSETS): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null;
  if (!tileImages[key]) {
    const img = new Image();
    img.src = versionedAssetUrl(HUB_TILE_ASSETS[key]);
    tileImages[key] = img;
  }
  return tileImages[key] ?? null;
}

function loadHubImage(url: string): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null;
  const cached = hubImages.get(url);
  if (cached) return cached;
  const img = new Image();
  img.addEventListener('load', () => {
    window.dispatchEvent(new CustomEvent('gravelight:hub-assets-loaded'));
  }, { once: true });
  img.src = versionedAssetUrl(url);
  hubImages.set(url, img);
  return img;
}

export function waystationViewportForCanvas(dimensions: { width: number; height: number }): { tile: number; cols: number; rows: number } {
  const width = Math.max(780, Math.floor(dimensions.width));
  const height = Math.max(480, Math.floor(dimensions.height));
  const cols = clamp(Math.floor(width / WAYSTATION_ZOOMED_TILE_TARGET), WAYSTATION_MIN_COLS, WAYSTATION_MAX_COLS);
  const rows = clamp(Math.floor(height / WAYSTATION_ZOOMED_TILE_TARGET), WAYSTATION_MIN_ROWS, WAYSTATION_MAX_ROWS);
  return {
    cols,
    rows,
    tile: Math.floor(Math.min(width / cols, height / rows)),
  };
}

function canvasMetrics(canvas: HTMLCanvasElement, state: AppState): PlazaMetrics {
  const rect = canvas.getBoundingClientRect();
  const width = Math.max(780, Math.floor(rect.width || canvas.clientWidth || 1100));
  const height = Math.max(480, Math.floor(rect.height || canvas.clientHeight || 620));
  const viewport = waystationViewportForCanvas({ width, height });
  const { cols, rows, tile } = viewport;
  const local = localHubPosition(state);
  const camera = cameraForHubPosition(local.position, { cols, rows });
  return {
    tile,
    cols,
    rows,
    camera,
    originX: Math.floor((width - cols * tile) / 2),
    originY: Math.floor((height - rows * tile) / 2),
    width,
    height,
  };
}

function tileToScreen(pos: HubPosition, metrics: PlazaMetrics): { x: number; y: number } {
  return screenPointForHubWorld(pos, metrics);
}

function visibleRect(metrics: PlazaMetrics): Rect {
  return { x: metrics.camera.x, y: metrics.camera.y, width: metrics.cols, height: metrics.rows };
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function tileKind(pos: HubPosition): 'wall' | 'water' | 'edge' | 'path' | 'floor' {
  if (pos.y >= MAP_HEIGHT - 2) return 'water';
  if (WALL_RECTS.some((rect) => inRect(pos, rect))) return 'wall';
  if (pos.y === MAP_HEIGHT - 3) return 'edge';
  if (
    (pos.x >= 17 && pos.x <= 21) ||
    (pos.y >= 12 && pos.y <= 14) ||
    (pos.y >= 5 && pos.y <= 7 && pos.x >= 6 && pos.x <= 32) ||
    (pos.y >= 16 && pos.y <= 18 && pos.x >= 5 && pos.x <= 32)
  ) {
    return 'path';
  }
  return 'floor';
}

function drawTile(ctx: CanvasRenderingContext2D, pos: HubPosition, x: number, y: number, size: number): void {
  const kind = tileKind(pos);
  const imageKey = kind === 'wall' ? 'wall' : kind === 'path' ? 'path' : kind === 'floor' ? 'floor' : null;
  const image = imageKey ? loadTileImage(imageKey) : null;
  if (image?.complete && image.naturalWidth > 0) {
    ctx.drawImage(image, 0, 0, image.width, image.height, x, y, size, size);
    ctx.strokeStyle = '#2a221b';
    ctx.strokeRect(x + 0.5, y + 0.5, size - 1, size - 1);
    return;
  }
  if (kind === 'water') {
    ctx.fillStyle = '#02080b';
    ctx.fillRect(x, y, size, size);
    ctx.fillStyle = 'rgba(78, 130, 145, 0.22)';
    ctx.fillRect(x + 4, y + ((pos.x * 7) % 17), size - 8, 2);
    return;
  }
  const base = kind === 'wall' ? '#17110d' : kind === 'edge' ? '#08131a' : kind === 'path' ? '#201813' : '#16120f';
  ctx.fillStyle = base;
  ctx.fillRect(x, y, size, size);
  ctx.fillStyle = kind === 'floor' ? 'rgba(231, 196, 130, 0.045)' : 'rgba(231, 196, 130, 0.075)';
  const nick = (pos.x * 11 + pos.y * 17) % 23;
  ctx.fillRect(x + 7 + (nick % Math.max(1, size - 22)), y + 8 + ((nick * 3) % Math.max(1, size - 22)), Math.max(3, size * 0.1), 2);
  ctx.strokeStyle = kind === 'wall' ? '#2d2118' : '#2a221b';
  ctx.strokeRect(x + 0.5, y + 0.5, size - 1, size - 1);
}

function drawStationFallback(
  ctx: CanvasRenderingContext2D,
  station: HubStation,
  rect: { x: number; y: number; width: number; height: number },
  tile: number,
  nearby: boolean,
): void {
  const stroke = nearby ? '#f0c978' : 'rgba(224, 189, 114, 0.52)';
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  ctx.fillStyle = 'rgba(5,4,3,0.66)';
  ctx.strokeStyle = stroke;
  ctx.lineWidth = nearby ? 3 : 1.5;

  switch (station.id) {
    case 'run':
      ctx.fillStyle = '#171417';
      ctx.fillRect(rect.x + tile * 0.48, rect.y + tile * 0.28, rect.width - tile * 0.96, rect.height - tile * 0.44);
      ctx.strokeRect(rect.x + tile * 0.48, rect.y + tile * 0.28, rect.width - tile * 0.96, rect.height - tile * 0.44);
      ctx.fillStyle = '#050506';
      ctx.beginPath();
      ctx.arc(cx, rect.y + rect.height - tile * 0.26, Math.min(rect.width, rect.height) * 0.36, Math.PI, 0);
      ctx.lineTo(cx + tile * 1.25, rect.y + rect.height - tile * 0.26);
      ctx.lineTo(cx - tile * 1.25, rect.y + rect.height - tile * 0.26);
      ctx.fill();
      break;
    case 'spinner':
      ctx.fillStyle = '#16262c';
      ctx.beginPath();
      ctx.arc(cx, cy, Math.min(rect.width, rect.height) * 0.28, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      break;
    default:
      ctx.fillStyle = station.color;
      ctx.beginPath();
      ctx.moveTo(rect.x + tile * 0.24, rect.y + rect.height - tile * 0.18);
      ctx.lineTo(rect.x + rect.width - tile * 0.24, rect.y + rect.height - tile * 0.18);
      ctx.lineTo(rect.x + rect.width - tile * 0.52, rect.y + tile * 0.34);
      ctx.lineTo(rect.x + tile * 0.52, rect.y + tile * 0.34);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      break;
  }

  ctx.fillStyle = '#e0a13d';
  ctx.fillRect(rect.x + tile * 0.35, rect.y + rect.height - tile * 0.52, tile * 0.09, tile * 0.18);
  ctx.fillRect(rect.x + rect.width - tile * 0.44, rect.y + rect.height - tile * 0.52, tile * 0.09, tile * 0.18);
}

function drawStationLabel(ctx: CanvasRenderingContext2D, station: HubStation, metrics: PlazaMetrics, nearby: boolean): void {
  const maxX = metrics.camera.x + metrics.cols;
  const maxY = metrics.camera.y + metrics.rows;
  if (
    station.labelAnchor.x < metrics.camera.x ||
    station.labelAnchor.x > maxX ||
    station.labelAnchor.y < metrics.camera.y ||
    station.labelAnchor.y > maxY
  ) {
    return;
  }
  const anchor = screenPointForHubWorld(station.labelAnchor, metrics);
  if (anchor.x < -metrics.tile || anchor.y < -metrics.tile || anchor.x > metrics.width + metrics.tile || anchor.y > metrics.height + metrics.tile) {
    return;
  }

  ctx.font = `${Math.max(11, Math.floor(metrics.tile * 0.17))}px Georgia, serif`;
  const textWidth = ctx.measureText(station.title).width;
  const textH = Math.max(18, metrics.tile * 0.3);
  const labelW = Math.min(metrics.tile * 2.8, textWidth + 16);
  const labelX = anchor.x - labelW / 2;
  const labelY = anchor.y - textH * 0.72;
  const mapLeft = metrics.originX;
  const mapTop = metrics.originY;
  const mapRight = metrics.originX + metrics.cols * metrics.tile;
  const mapBottom = metrics.originY + metrics.rows * metrics.tile;
  const labelLeft = labelX;
  const labelRight = labelX + labelW;
  const labelTop = labelY;
  const labelBottom = labelY + textH;
  if (labelLeft < mapLeft || labelRight > mapRight || labelTop < mapTop || labelBottom > mapBottom) {
    return;
  }

  if (HUB_LABEL_SIGNPOST_ENABLED) {
    const signSize = metrics.tile * 0.66;
    const sign = loadHubImage(HUB_ENVIRONMENT_PROP_ASSETS.hub_prop_signpost_01);
    if (sign?.complete && sign.naturalWidth > 0) {
      ctx.drawImage(sign, 0, 0, sign.width, sign.height, anchor.x - signSize / 2, anchor.y - signSize * 0.12, signSize, signSize);
    }
  }
  if (HUB_LABEL_BACKING_ENABLED) {
    ctx.fillStyle = nearby ? 'rgba(35,24,13,0.94)' : 'rgba(8,7,6,0.88)';
    ctx.strokeStyle = nearby ? '#f0c978' : 'rgba(224,189,114,0.45)';
    ctx.lineWidth = nearby ? 2 : 1;
    ctx.fillRect(labelX, labelY, labelW, textH);
    ctx.strokeRect(labelX + 0.5, labelY + 0.5, labelW - 1, textH - 1);
  }
  if (HUB_LABEL_WAX_SEAL_ENABLED) {
    ctx.fillStyle = 'rgba(128, 34, 30, 0.72)';
    ctx.beginPath();
    ctx.arc(labelX + labelW - 9, labelY + textH - 8, Math.min(metrics.tile * 0.08, 5), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = nearby ? 4 : 3;
  ctx.strokeStyle = 'rgba(3,2,1,0.92)';
  ctx.shadowColor = 'rgba(0,0,0,0.78)';
  ctx.shadowBlur = nearby ? 5 : 3;
  ctx.shadowOffsetY = 1;
  ctx.strokeText(station.title, anchor.x, labelY + textH / 2, labelW - 8);
  ctx.fillStyle = nearby ? '#ffe6ad' : '#f5dfb6';
  ctx.fillText(station.title, anchor.x, labelY + textH / 2, labelW - 8);
}

function drawStation(ctx: CanvasRenderingContext2D, station: HubStation, metrics: PlazaMetrics, nearby: boolean): void {
  const rect = station.footprint;
  if (!rectsOverlap(rect, visibleRect(metrics))) return;
  const topLeft = tileToScreen({ x: rect.x, y: rect.y }, metrics);
  const width = rect.width * metrics.tile;
  const height = rect.height * metrics.tile;
  const baseX = topLeft.x + width / 2 + (station.prop.anchorOffsetTiles?.x ?? 0) * metrics.tile;
  const baseY = topLeft.y + height - metrics.tile * 0.08 + (station.prop.anchorOffsetTiles?.y ?? 0) * metrics.tile;
  ctx.save();
  ctx.globalAlpha = station.devOnly ? 0.86 : 1;
  ctx.fillStyle = 'rgba(0,0,0,0.44)';
  ctx.beginPath();
  ctx.ellipse(baseX, baseY, width * 0.42, metrics.tile * 0.18, 0, 0, Math.PI * 2);
  ctx.fill();

  if (nearby) {
    ctx.fillStyle = 'rgba(224, 161, 61, 0.14)';
    ctx.beginPath();
    ctx.ellipse(
      baseX,
      baseY - metrics.tile * 0.08,
      width * 0.34,
      metrics.tile * 0.2,
      0,
      0,
      Math.PI * 2,
    );
    ctx.fill();
    ctx.strokeStyle = 'rgba(224, 161, 61, 0.52)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(
      baseX,
      baseY - metrics.tile * 0.08,
      width * 0.36,
      metrics.tile * 0.22,
      0,
      0,
      Math.PI * 2,
    );
    ctx.stroke();
  }

  const asset = loadHubImage(HUB_STATION_PROP_ASSETS[station.prop.assetId]);
  if (asset?.complete && asset.naturalWidth > 0) {
    ctx.imageSmoothingEnabled = false;
    const scale = Math.min(width / asset.width, height / asset.height);
    const drawWidth = asset.width * scale;
    const drawHeight = asset.height * scale;
    ctx.drawImage(asset, 0, 0, asset.width, asset.height, baseX - drawWidth / 2, baseY - drawHeight, drawWidth, drawHeight);
  } else {
    const drawRect = {
      x: topLeft.x,
      y: topLeft.y - metrics.tile * 0.06,
      width,
      height: height + metrics.tile * 0.06,
    };
    drawStationFallback(ctx, station, drawRect, metrics.tile, nearby);
  }

  drawStationLabel(ctx, station, metrics, nearby);
  ctx.restore();
}

function drawDecorProp(ctx: CanvasRenderingContext2D, metrics: PlazaMetrics, prop: HubDecorProp): void {
  const propRect = prop.footprint ?? {
    x: Math.floor(prop.position.x - prop.drawWidthTiles / 2),
    y: Math.floor(prop.position.y - 1),
    width: Math.ceil(prop.drawWidthTiles),
    height: 2,
  };
  if (!rectsOverlap(propRect, visibleRect(metrics))) return;
  const base = screenPointForHubWorld(prop.position, metrics);
  const x = base.x;
  const y = base.y;
  if (x < -metrics.tile * 2 || y < -metrics.tile * 2 || x > metrics.width + metrics.tile * 2 || y > metrics.height + metrics.tile * 2) return;
  const image = loadHubImage(HUB_ENVIRONMENT_PROP_ASSETS[prop.assetId]);
  const drawWidth = metrics.tile * prop.drawWidthTiles;
  const drawHeight = drawWidth * (prop.sourceHeight / prop.sourceWidth);
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.34)';
  ctx.beginPath();
  ctx.ellipse(x, y + metrics.tile * 0.06, drawWidth * 0.34, metrics.tile * 0.12, 0, 0, Math.PI * 2);
  ctx.fill();
  if (image?.complete && image.naturalWidth > 0) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(image, 0, 0, image.width, image.height, x - drawWidth / 2, y - drawHeight, drawWidth, drawHeight);
    ctx.restore();
    return;
  }
  ctx.fillStyle = prop.assetId.includes('pier') ? '#0a1518' : '#4a3421';
  ctx.fillRect(x - drawWidth / 2, y - drawHeight * 0.65, drawWidth, drawHeight * 0.55);
  if (prop.assetId.includes('brazier') || prop.assetId.includes('candle') || prop.assetId.includes('lantern')) {
    ctx.fillStyle = '#e0a13d';
    ctx.beginPath();
    ctx.arc(x, y - drawHeight * 0.52, metrics.tile * 0.08, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawActor(
  ctx: CanvasRenderingContext2D,
  metrics: PlazaMetrics,
  pos: HubPosition,
  facing: HubFacing,
  label: string,
  opts: {
    self?: boolean;
    cosmetics?: readonly EquippedCosmetic[];
    emote?: string | null;
    emoteStartedAtMs?: number | null;
    emoteExpiresAtMs?: number | null;
    alpha?: number;
  } = {},
): void {
  if (pos.x < metrics.camera.x - 1 || pos.y < metrics.camera.y - 1 || pos.x > metrics.camera.x + metrics.cols || pos.y > metrics.camera.y + metrics.rows) {
    return;
  }
  const topLeft = tileToScreen(pos, metrics);
  const cx = topLeft.x + metrics.tile / 2;
  const feetY = topLeft.y + metrics.tile * 0.82;
  const image = loadPlayerImage(playerSpriteForCosmetics(opts.cosmetics));
  const frame = waystationIdleFrameAt(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const srcX = frame * PLAYER_FRAME;
  // Self and remote actors share ONE geometry helper — do not fork scale/anchor per path.
  const { dx, dy, width: actorW, height: actorH } = waystationActorGeometry(metrics.tile, cx, feetY);
  const ring = waystationActorRingGeometry(metrics.tile);
  ctx.save();
  ctx.globalAlpha = opts.alpha ?? 1;
  ctx.fillStyle = opts.self ? 'rgba(224, 161, 61, 0.28)' : 'rgba(90, 155, 216, 0.18)';
  ctx.beginPath();
  ctx.ellipse(cx, feetY, ring.radiusX, ring.radiusY, 0, 0, Math.PI * 2);
  ctx.fill();
  if (opts.self) {
    // Highlight stroke on the same ring ellipse — a color cue, not a size cue.
    ctx.strokeStyle = '#e0a13d';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(cx, feetY, ring.radiusX, ring.radiusY, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  if (image?.complete && image.naturalWidth > 0) {
    ctx.imageSmoothingEnabled = false;
    if (facing === 'west') {
      ctx.save();
      ctx.translate(dx + actorW, dy);
      ctx.scale(-1, 1);
      ctx.drawImage(image, srcX, 0, PLAYER_FRAME, PLAYER_FRAME, 0, 0, actorW, actorH);
      ctx.restore();
    } else {
      ctx.drawImage(image, srcX, 0, PLAYER_FRAME, PLAYER_FRAME, dx, dy, actorW, actorH);
    }
  } else {
    ctx.fillStyle = opts.self ? '#e0a13d' : '#5a9bd8';
    ctx.fillRect(cx - metrics.tile * 0.16, feetY - metrics.tile * 0.78, metrics.tile * 0.32, metrics.tile * 0.68);
    ctx.fillStyle = '#080706';
    ctx.fillRect(cx - metrics.tile * 0.1, feetY - metrics.tile * 0.68, metrics.tile * 0.2, metrics.tile * 0.18);
  }
  ctx.fillStyle = 'rgba(5,4,3,0.84)';
  ctx.strokeStyle = 'rgba(224,189,114,0.28)';
  const text = label.slice(0, 12);
  ctx.font = `${Math.max(10, Math.floor(metrics.tile * 0.16))}px ui-monospace, monospace`;
  const tw = Math.min(metrics.tile * 1.45, ctx.measureText(text).width + 12);
  ctx.fillRect(cx - tw / 2, feetY + 4, tw, 18);
  ctx.strokeRect(cx - tw / 2, feetY + 4, tw, 18);
  ctx.fillStyle = '#f5dfb6';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, cx, feetY + 13, tw - 4);
  drawActorEmote(ctx, metrics, opts, cx, dy);
  ctx.restore();
}

/**
 * Timed emote bubble: the shared thought-bubble frame sits diagonally
 * above-right of the actor's head with the selected emote animating inside.
 * It follows the actor (drawn relative to the actor each frame) and stops
 * rendering the moment its expiry passes — even between roster polls.
 * Unknown/legacy emote ids fall back to the old small text bubble.
 */
function drawActorEmote(
  ctx: CanvasRenderingContext2D,
  metrics: PlazaMetrics,
  opts: { emote?: string | null; emoteStartedAtMs?: number | null; emoteExpiresAtMs?: number | null },
  cx: number,
  headY: number,
): void {
  if (!opts.emote) return;
  const nowMs = Date.now();
  if (opts.emoteExpiresAtMs !== undefined && opts.emoteExpiresAtMs !== null && nowMs >= opts.emoteExpiresAtMs) return;

  const def = hubEmoteById(opts.emote);
  const bubbleImage = loadHubImage(emoteSpriteUrl(HUB_EMOTE_BUBBLE_ASSET_ID));
  const emoteImage = def ? loadHubImage(emoteSpriteUrl(def.assetId)) : null;
  const bubbleReady = !!bubbleImage?.complete && bubbleImage.naturalWidth > 0;
  const emoteReady = !!emoteImage?.complete && emoteImage.naturalWidth > 0;

  if (def && bubbleReady && emoteReady) {
    ctx.imageSmoothingEnabled = false;
    const bubbleSize = metrics.tile * 1.0;
    // Diagonally to the right of the actor's head; the bubble tail points back
    // down-left toward the actor.
    const bx = cx + metrics.tile * 0.22;
    const by = headY - bubbleSize * 0.92;
    ctx.drawImage(bubbleImage!, 0, 0, bubbleImage!.naturalWidth, bubbleImage!.naturalHeight, bx, by, bubbleSize, bubbleSize);
    const frameSize = emoteImage!.naturalHeight;
    const frames = Math.max(1, Math.floor(emoteImage!.naturalWidth / Math.max(1, frameSize)));
    const frame = emoteFrameAt({ frames: Math.min(frames, def.frames), fps: def.fps }, opts.emoteStartedAtMs ?? null, nowMs);
    const inner = bubbleSize * 0.6;
    const ix = bx + (bubbleSize - inner) / 2;
    const iy = by + bubbleSize * 0.14;
    ctx.drawImage(emoteImage!, frame * frameSize, 0, frameSize, frameSize, ix, iy, inner, inner);
    return;
  }

  // Fallback (assets still loading or legacy cosmetic emote): small text bubble.
  ctx.fillStyle = 'rgba(224,161,61,0.92)';
  ctx.fillRect(cx - metrics.tile * 0.38, headY - 18, metrics.tile * 0.76, 18);
  ctx.fillStyle = '#070605';
  ctx.font = `${Math.max(10, Math.floor(metrics.tile * 0.15))}px ui-sans-serif, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(def?.label ?? opts.emote, cx, headY - 9, metrics.tile * 0.72);
}

function setCanvasSize(canvas: HTMLCanvasElement, metrics: PlazaMetrics): CanvasRenderingContext2D | null {
  const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
  const pxWidth = metrics.width * dpr;
  const pxHeight = metrics.height * dpr;
  if (canvas.width !== pxWidth || canvas.height !== pxHeight) {
    canvas.width = pxWidth;
    canvas.height = pxHeight;
  }
  canvas.style.width = `${metrics.width}px`;
  canvas.style.height = `${metrics.height}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.imageSmoothingEnabled = false;
  return ctx;
}

export function drawWaystationPlaza(canvas: HTMLCanvasElement, state: AppState): void {
  const metrics = canvasMetrics(canvas, state);
  const ctx = setCanvasSize(canvas, metrics);
  if (!ctx) return;
  ctx.clearRect(0, 0, metrics.width, metrics.height);
  ctx.fillStyle = '#050505';
  ctx.fillRect(0, 0, metrics.width, metrics.height);

  ctx.save();
  ctx.beginPath();
  ctx.rect(metrics.originX, metrics.originY, metrics.cols * metrics.tile, metrics.rows * metrics.tile);
  ctx.clip();

  for (let y = metrics.camera.y; y < metrics.camera.y + metrics.rows; y += 1) {
    for (let x = metrics.camera.x; x < metrics.camera.x + metrics.cols; x += 1) {
      const screen = tileToScreen({ x, y }, metrics);
      drawTile(ctx, { x, y }, screen.x, screen.y, metrics.tile);
    }
  }

  const local = localHubPosition(state);
  const nearby = nearbyStationForPosition(local.position, state.devMode);
  for (const prop of HUB_DECOR_PROPS) {
    drawDecorProp(ctx, metrics, prop);
  }
  for (const station of HUB_STATIONS) {
    if (station.devOnly && !state.devMode) continue;
    drawStation(ctx, station, metrics, nearby?.id === station.id);
  }

  const selfId = state.hub?.self.accountId;
  const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
  for (const remote of currentRemotePresences(state, nowMs).values()) {
    drawActor(ctx, metrics, { x: remote.renderX, y: remote.renderY }, remote.facing, remote.walletLabel, {
      cosmetics: remote.cosmetics,
      emote: remote.emote,
      emoteStartedAtMs: remote.emoteStartedAtMs,
      emoteExpiresAtMs: remote.emoteExpiresAtMs,
      alpha: remotePresenceAlpha(remote, nowMs),
    });
  }
  const selfPresence = state.hub?.presence.find((entry) => entry.accountId === selfId);
  drawActor(ctx, metrics, local.position, local.facing, selfPresence?.walletLabel ?? state.hub?.self.walletLabel ?? 'you', {
    self: true,
    cosmetics: selfPresence?.cosmetics ?? state.equippedCosmetics ?? [],
    emote: selfPresence?.emote,
    emoteStartedAtMs: parseEpochMs(selfPresence?.emoteStartedAt),
    emoteExpiresAtMs: parseEpochMs(selfPresence?.emoteExpiresAt),
  });
  ctx.restore();

  ctx.save();
  const vignette = ctx.createRadialGradient(metrics.width / 2, metrics.height / 2, metrics.width * 0.18, metrics.width / 2, metrics.height / 2, metrics.width * 0.72);
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.42)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, metrics.width, metrics.height);
  ctx.restore();
}

export function hubCanvasClickTarget(
  canvas: HTMLCanvasElement,
  state: AppState,
  clientX: number,
  clientY: number,
): { kind: 'player'; accountId: string } | { kind: 'station'; stationId: HubStationId } | null {
  const rect = canvas.getBoundingClientRect();
  const metrics = canvasMetrics(canvas, state);
  const x = clientX - rect.left - metrics.originX;
  const y = clientY - rect.top - metrics.originY;
  const tile = {
    x: metrics.camera.x + Math.floor(x / metrics.tile),
    y: metrics.camera.y + Math.floor(y / metrics.tile),
  };
  if (state.hub?.access === 'full') {
    const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    for (const remote of currentRemotePresences(state, nowMs).values()) {
      if (Math.abs(remote.renderX - tile.x) <= 1 && Math.abs(remote.renderY - tile.y) <= 1) {
        return { kind: 'player', accountId: remote.accountId };
      }
    }
  }
  const station = stationAtHubTile(tile, state.devMode);
  if (station) return { kind: 'station', stationId: station.id };
  return null;
}
